import ActionDropdown from "@/CommonElements/ActionDropdown";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { SearchFilter } from "@/CommonElements/SearchFilter/SearchFilter";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import BtnAsLink from "@/Component/BtnAsLink";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { Info } from "@/Component/Info";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { SelectFlightDetails } from "@/Component/SelectFlightDetails";
import { LabelValue } from "@/Component/Value";
import { ArrivalCard } from "@/Pages/Makkah/Reception/ArrivalCard";
import { numbersOnlyRules } from "@/Pages/ProfileForm";
import { useGetMadinaArrivalsQuery, useMadinaCreateReportMutation, useMadinaDeleteBagMutation, useMadinaDeleteReportMutation, useMadinaMarkAsArrivedMutation, useMadinaMarkAsCancelledMutation, useMadinaOneReportQuery, useMadinaPrintBagListMutation, useMadinaPrintBagMutation, useMadinaUpdateReportMutation } from "@/services/arrivals/madinah";
import { useCreateBagMutation, useGetBagsQuery, useUpdateBagMutation } from "@/services/madinah/bag";
import { DATE_TIME_FORMAT, DATE_TIME_FORMAT_INPUT, MODES, NUSUK_CITY_ID } from "@/utils/constants";
import { cn, getDayNameInArabic, handleFormErrors, joinValues, PickKeyPairs } from "@/utils/helpers";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useScreenSize } from "@/utils/hooks/useScreenSize ";
import { AirplaneLanding01Icon, DeleteIcon, Edit02Icon, Files01Icon, PencilEdit01Icon, PrinterIcon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, DatePicker, Divider, Empty, Flex, Input, Select, Space, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { produce } from "immer";
import { useEffect, useRef, useState } from "react";
import { Button, Modal, Row, Stack } from "react-bootstrap";
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";

const { TextArea } = Input
export const MadinaReceptionList = () => {
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBag, setSelectedBag] = useState(null);
  const [arrival, setArrival] = useState(null);
  const [isBagModalOpen, setIsBagModalOpen] = useState(false);

  const { t } = useTranslation();
  const { count, ...filtersValue } = filters;
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  // =================
  // Side Effects
  // =================
  const params = {
    port: "MED",
    ...filtersValue,
    page: currentPage,
    per_page: 10,
  }
  const { mutate: deleteReportMutation, isPending: isDeleteReportPending, variables: deletingVariables, isSuccess: isDeleteReportSuccess } = useMadinaDeleteReportMutation();
  const { printBagsMutation, isPrintBagPending } = usePrintBagList();
  const { data: arrivalsRes, isLoading } = useGetMadinaArrivalsQuery(params);
  const { mutate: markAsArrived, isPending: isMarkAsArrivedPending } = useMadinaMarkAsArrivedMutation(params);
  const { mutate: markAsCancelled, isPending: isMarkAsCancelledPending } = useMadinaMarkAsCancelledMutation(params);

  const arrivals = isLoading
    ? Array.from({ length: 10 }, (_, index) => ({ id: index, loading: true }))
    : arrivalsRes?.data?.data || [];
  const meta = arrivalsRes?.data?.meta || {};

  const labelValues = [
    {
      key: "capacity",
      label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
    },
    {
      key: "airline_name",
      label: t("ARRIVAL_CARD_AIRLINE_COMPANY"),
      render: (_, arrival) =>
        getLocalizedText(arrival?.airline_name_ar, arrival?.airline_name_en) ||
        "-",
    },
    {
      key: "airport_id",
      label: t("UPCOMING_COL_PORT"),
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          optionKey="ports"
          campareBy="id"
          render={(option) =>
            getLocalizedText(option?.name_ar, option?.name_en) || option?.name
          }
        />
      ),
    },
    {
      key: "flight_number",
      label: t("FLIGHTS_MODAL_FLIGHT_NO_LABEL"),
    },
    {
      key: "departure_time",
      label: t("AIRPORT_DEPARTURE_TIME"),
      render: (value) => {
        return value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-";
      },
    },
    {
      key: "arrived_at",
      label: t("FLIGHTS_MODAL_ARRIVAL_TIME_LABEL"),
      render: (value) => {
        return value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-";
      },
    },
    {
      key: "contract_id",
      label: t("HOUSING_CONTRACT"),
      render: (value) => {
        return <Space>
          <Info title={<SelectCachedOptions.Display
            value={value}
            optionKey="contracts"
            campareBy="id"
            render={(option) => <LabelValue.Value value={joinValues([option?.address])} />}
          />}
          />
          {value}
        </Space>
      },
    },
    {
      key: "organizer",
      label: t("ORGANIZER"),
    },
    {
      key: "from_city_name",
      label: t("DEPARTURE_CITY"),
    },
    {
      key: "to_city_name",
      label: t("ARRIVAL_CITY"),
    },
    {
      key: "bags_count",
      label: t("BAGS_COUNT"),
    }
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDebouncedChange = (searchValue) => {
    setFilters(searchValue);
    setCurrentPage(1);
  };
  const filterFields = [
    {
      name: "request_number",
      label: t("ARRIVAL_CARD_REQUEST_NUMBER"),
      type: "text",
      placeholder: t("ARRIVAL_CARD_REQUEST_NUMBER"),
    },
    {
      name: "flight_date",
      label: t("FLIGHT_DATE"),
      type: "date",
    },
    {
      name: "arrival_date_from",
      type: "date",
      optionKey: "directions",
      label: t("DATE_FROM"),
    },
    {
      name: "arrival_date_to",
      type: "date",
      optionKey: "directions",
      label: t("DATE_TO"),
    },
    {
      name: "direction_id",
      type: "select",
      optionKey: "directions",
      label: t("DIRECTION"),
      filter: (d) => d.id === 2 || d.id === 1,
      options: (d) => ({
        value: d.id,
        label: d.name,
      }),
    },
    {
      name: "contract_id",
      type: "select",
      optionKey: "contracts",
      label: t("HOUSING_CONTRACT"),
      filter: (c) => String(c.city_id) === String(NUSUK_CITY_ID.madina),
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),

    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "airport_code",
      label: t("UPCOMING_COL_PORT"),
      type: "select",
      optionKey: "ports",
      options: (n) => ({
        value: n.code,
        label: [
          getLocalizedText(n?.name_ar, n?.name_en) || n?.name,
          n?.code,
        ].join(" - "),
      }),
    },
  ];

  // =================
  // Rendering
  // =================
  const no_results = !arrivals.length && !isLoading && !count;
  const no_filter_results = !!count && !arrivals.length;


  const { confirm, ModalComponent } = useConfirm();
  // Create action buttons for each arrival
  const createActions = (arrival, index) => {
    const viewBagsButton = (
      <>

        {!!arrival.arrived_at && <Button
          className="btn-danger flex-fill"
          onClick={() => {
            confirm({
              title: t("CONFIRM"),
              content: <Trans i18nKey="CONFIRM_CANCEL_FLIGHT" components={{ red: <span style={{ color: "#FF0000", fontWeight: "bold" }} /> }} />,
              onOk: ({ closeModal }) => {
                markAsCancelled({ flight_id: arrival.id }, {
                  onSuccess: () => {
                    closeModal();
                  },
                });
                return false;
              }
            })

          }}
          disabled={isMarkAsCancelledPending}
        >
          {t("ARRIVAL_CARD_CANCEL_FLIGHT")}
        </Button>}
        {!arrival.arrived_at && <Button
          className="btn-primary flex-fill"
          onClick={() => {
            confirm({
              title: t("CONFIRM"),
              content: <Trans i18nKey="CONFIRM_MARK_ARRIVED" components={{ green: <span style={{ color: "#166433", fontWeight: "bold" }} /> }} />,
              onOk: ({ closeModal }) => {
                markAsArrived({ flight_id: arrival.id }, {
                  onSuccess: () => {
                    closeModal();
                  },
                });
                return false;
              }
            })

          }}
          disabled={isMarkAsArrivedPending}
        >
          {t("ARIVED_AIRPORT")}
        </Button>}
        <Button
          className="btn btn-outline btn-secondary flex-fill"
          onClick={() => {
            setArrival({ ...arrival, index });
            setIsBagModalOpen(true);
          }}
        >
          {t("MANAGE_BAGS")}
        </Button>
      </>
    );

    const cancelFlightButton = (
      <Button
        className="btn-danger flex-fill"
        onClick={() => {
          // setSelectedArrival(arrival);
          // setIsCancelConfirmOpen(true);
        }}
      // disabled={cancelFlightMutation.isPending}
      >
        {t("ARRIVAL_CARD_CANCEL_FLIGHT")}
      </Button>
    );

    const markArrivedFlight = (
      <Button
        className="btn-primary flex-fill"
        onClick={() => {
          // setSelectedArrival(arrival);
          // setIsConfirmOpen(true);
        }}
      // disabled={markAsArrivedMutation.isPending}
      >
        {t("ARIVED_AIRPORT")}
      </Button>
    );

    const restoreFlight = (
      <Button
        onClick={() => {
          // setSelectedArrival(arrival);
          // setIsRestoreConfirmOpen(true);
        }}
        className="btn-outline btn-brand flex-fill"
      // disabled={restoreFlightMutation.isPending}
      >
        {t("RESTORE_FLIGHT")}
      </Button>
    );

    switch (arrival.status) {
      case "arrived_airport":
        return (
          <>
            {viewBagsButton}
            {cancelFlightButton}
          </>
        );
      case "not_arrived":
        return (
          <>
            {markArrivedFlight}
            {cancelFlightButton}
          </>
        );
      case "cancelled":
        return <>{restoreFlight}</>;
      default:
        return (
          <>
            {/* {markArrivedFlight} */}
            {viewBagsButton}
            {/* {cancelFlightButton} */}
          </>
        );
    }
  };


  const handleCloseBagModal = () => {
    setIsBagModalOpen(false);
    setArrival(null);
  }

  const handleSelectBag = (bag) => {
    setSelectedBag(bag);

  }
  const handleCloseUpdateBagModal = () => {
    setSelectedBag(null);
  }

  const clearArrival = () => {
    setArrival(null);
  }
  const clearBags = () => {
    setSelectedBag(null);
  }


  const handleDeleteRequest = (id) => {

    confirm({
      title: t("CONFIRM"),
      content: <Trans
        i18nKey="CONFIRM_DELETE_REQUEST_MESSAGE"
        components={{ danger: <span style={{ color: "#FF0000", fontWeight: "bold" }} /> }} />,
      okText: t("CONFIRM"),
      cancelText: t("CANCEL"),
      onOk: ({ closeModal }) => {
        deleteReportMutation({ id },
          {
            onSuccess: () => {
              closeModal();
            },
          }
        );

        return false;
      },

      onCancel: () => { },
    });


  }

  const navigate = useNavigate();
  const location = useLocation();
  const search = location.search;

  const arrivalRequestActions = (arrival) => [
    {
      label: t("EDIT"),
      onClick: () => navigate(`/app/madina/reception/${arrival.id}/edit${search}`),
      disabled: arrival.is_created_manually === 0 || undefined,
      icon: <HugeiconsIcon size={14} icon={PencilEdit01Icon} />
    },
    {
      label: t("PRINT_REQUEST_BAGS"),
      onClick: () => printBagsMutation({ request_number: arrival.request_number }),
      disabled: isPrintBagPending || undefined,
      icon: <HugeiconsIcon size={14} icon={PrinterIcon} />
    },
    {
      type: "divider",
    },
    {
      label: t("DELETE"),
      onClick: () => handleDeleteRequest(arrival.request_number),
      disabled: isDeleteReportPending || undefined,
      icon: <HugeiconsIcon size={14} icon={DeleteIcon} />,
      danger: true,
      style: {
        textWrap: "nowrap",
      }
    },
  ]


  return (
    <Stack gap={2}>
      <ModalComponent loading={isMarkAsArrivedPending || isMarkAsCancelledPending || isDeleteReportPending} />
      <Flex gap={8} align="center">

        <div className="flex-fill">
          <SearchFilter
            filterFields={filterFields}
            onChange={handleDebouncedChange}
            debounced
          />
        </div>
        <RequestModalCRUD />
      </Flex>

      {no_results && (
        <Empty
          image={<HugeiconsIcon size={40} icon={AirplaneLanding01Icon} />}
          description={t("NO_ARRIVALS_FOUND_TODAY")}
        />
      )}

      {no_filter_results && (
        <Empty
          image={<HugeiconsIcon size={40} icon={AirplaneLanding01Icon} />}
          description={t("NO_FILTER_RESULTS")}
        />
      )}
      <Row>
        {arrivals.map((arrival) => {
          return (
            <ArrivalCard
              bottomOffset={20}
              loading={arrival.loading}
              key={arrival.id}
              arrival={arrival}
              groupName={arrival?.name||""}
              title={t("ARRIVAL_CARD_REQUEST_NUMBER")}
              statusConfig={{
                color: arrival.arrived_at ? "success" : "brand",
                icon: AirplaneLanding01Icon,
                text: !!arrival.arrived_at ? t("ARRIVED_AIRPORT") : t("NOT_ARRIVED_YET"),
              }}

              renderActions={() => (
                <ActionDropdown
                  removeContainer={true}
                  loading={(isDeleteReportPending) && (
                    arrival.request_number === deletingVariables?.request_number)}
                  items={arrivalRequestActions(arrival)}
                  iconAlign="vertical"
                  buttonClassName="outline btn-icon btn-xs"
                />
              )}
              titleValue={
                <Flex gap={8} align="center">
                  {arrival.request_number}
                </Flex>
              }
              actions={createActions(arrival)}
              labelValues={labelValues}
            />
          );
        })}
      </Row>

      <ActionsFooter >
        <AntPagination
          className="sticky-bottom"
          total={meta?.total || 0}
          pageSize={meta?.per_page || 10}
          current={meta?.current_page || 1}
          onChange={handlePageChange}
          disabled={isLoading}
        />
      </ActionsFooter>


      <Modal scrollable show={isBagModalOpen && !!arrival} onHide={handleCloseBagModal} centered size="lg" backdrop="static" keyboard={false} >
        <Modal.Body>
          <div style={{ background: "white", position: "relative" }}>
            <CreateBagModal {...{ arrival, confirm, handleCloseBagModal, handleSelectBag, bag: selectedBag, handleCloseUpdateBagModal, params, clearArrival, clearBags }} />
          </div>
        </Modal.Body>
      </Modal>
    </Stack >
  );
};
const bagDefaultValues = {
  bag_number: undefined,
  expected_pilgrims_count: undefined,
  actual_passports_count: undefined,
  passport_images_count: undefined,
  pilgrims_without_passport_count: undefined,
  classification_type: undefined,
  notes: undefined,
  nationality_passport_details: [],
};

const nationalityPassportDetailDefaultRow = {
  nationality_id: undefined,
  nationality_name: "",
  actual_passports_count: undefined,
  passport_images_count: undefined,
  pilgrims_without_passport_count: undefined,
};
export const BagsForm = (props) => {
  const form = useFormContext();
  const { control, formState: { errors } } = form;
  const { isViewMode = false, isLoading = false } = props;
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "nationality_passport_details",
  });
  const { isDesktop } = useScreenSize()

  const ctls = [
    {
      controllerProps: {
        name: "bag_number",
        placeholder: t("BAG_NUMBER"),
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="bag_number"
            placeholder={t("BAG_NUMBER")}
            status={errors.bag_number ? "error" : undefined}
          />
        ),
      },
      labelProps: { children: t("BAG_NUMBER"), className: "required" },

    },
    {
      controllerProps: {
        name: "expected_pilgrims_count",
        placeholder: t("EXPECTED_PILGRIMS_COUNT"),
        rules: {
          required: t("FIELD_REQUIRED"),
          ...numbersOnlyRules(t),
          max: {
            value: 50,
            message: t("MAX_LENGTH", { max: 50 }),
          },
          min: {
            value: 1,
            message: t("MIN_LENGTH", { min: 1 }),
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="expected_pilgrims_count"
            placeholder={t("EXPECTED_PILGRIMS_COUNT")}
            status={errors.expected_pilgrims_count ? "error" : undefined}
          />
        ),
      },
      labelProps: { children: t("EXPECTED_PILGRIMS_COUNT"), className: "required" },

    },
    {
      controllerProps: {
        name: "classification_type",
        placeholder: t("CLASSIFICATION_TYPE"),
        render: ({ field }) => (
          <SelectGroups {...field} />
        ),
      },
      labelProps: { children: t("GROUPS"), },
    },
    {
      controllerProps: {
        name: "notes",
        placeholder: t("NOTES"),
        render: ({ field }) => (
          <TextArea
            {...field}
            placeholder={t("NOTES")}
            status={errors.notes ? "error" : undefined}
          />
        ),
      },
      labelProps: { children: t("NOTES"), },
    },
  ];




  const validateActualPassportsCount = (value) => {
    const nationalityPassportDetails = form.watch("nationality_passport_details")
    const totalActualPassportsCount = nationalityPassportDetails.reduce((acc, curr) => +acc + +(curr.actual_passports_count || 0) + +(curr.passport_images_count || 0) + +(curr.pilgrims_without_passport_count || 0), 0);
    const expectedPilgrimsCount = +form.watch("expected_pilgrims_count");

    if (totalActualPassportsCount !== expectedPilgrimsCount) {
      form.setError("nationality_passport_details", {
        type: "validate",
        message: t("TOTAL_NATIONALITY_PASSPORTS_COUNT_NOT_EQUAL_TO_EXPECTED_PILGRIMS_COUNT", {
          totalCount: totalActualPassportsCount,
          expectedCount: expectedPilgrimsCount,
        }),
      })
    } else {
      form.clearErrors("nationality_passport_details");
    }

    return true;
  }


  const checkNationalityPassportDetailsValidateError = (errors) => {
    return errors?.nationality_passport_details && errors?.nationality_passport_details?.type === "validate";

  }


  const nationalityPassportColumns = [
    {
      name: t("NATIONALITY"),
      key: "nationality_id",
      render: ($, $$, index) => (
        <Controller
          name={`nationality_passport_details.${index}.nationality_id`}
          control={control}
          rules={{ required: t("FIELD_REQUIRED") }}
          render={({ field, fieldState: { error } }) => (
            <Tooltip
              color="red"
              placement={isDesktop ? "left" : "top"}
              open={error}
              title={error?.message}
            >
              <SelectCachedOptions
                {...field}
                style={{ width: "215px" }}
                status={errors?.nationality_passport_details?.[index]?.nationality_id ? "error" : undefined}
                id="nationality_id"
                name="nationality_id"
                showSearch
                placeholder={t("CAMPAIGNS_FORM_COUNTRY_PLACEHOLDER")}
                optionKey="nationalities"
                options={(country) => ({
                  value: String(country.id),
                  label: <SelectCountry.Display value={country.id} />,
                })}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      name: t("ACTUAL_PASSPORTS_COUNT"),
      key: "actual_passports_count",
      render: ($, $$, index) => (
        <Controller
          name={`nationality_passport_details.${index}.actual_passports_count`}
          control={control}
          rules={{
            required: t("FIELD_REQUIRED"), ...numbersOnlyRules(t),
            validate: validateActualPassportsCount,
          }}
          render={({ field, fieldState: { error }, formState: { errors } }) => (
            <Tooltip
              color="red"
              placement={isDesktop ? "left" : "top"}
              open={error}
              title={error?.message}
            >
              <AntInput
                {...field}
                placeholder={t("ACTUAL_PASSPORTS_COUNT")}
                status={checkNationalityPassportDetailsValidateError(errors) ? "error" : undefined}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      name: t("PASSPORT_IMAGES_COUNT"),
      key: "passport_images_count",
      render: ($, $$, index) => (
        <Controller
          name={`nationality_passport_details.${index}.passport_images_count`}
          control={control}
          rules={{
            required: t("FIELD_REQUIRED"), ...numbersOnlyRules(t),
            validate: validateActualPassportsCount,
          }}
          render={({ field, fieldState: { error, invalid }, formState: { errors } }) => {

            return (
              <Tooltip
                color="red"
                placement={isDesktop ? "left" : "top"}
                open={error}
                title={error?.message}
              >
                <AntInput
                  {...field}

                  placeholder={t("PASSPORT_IMAGES_COUNT")}
                  status={invalid || checkNationalityPassportDetailsValidateError(errors) ? "error" : undefined}
                />
              </Tooltip>
            )
          }}
        />
      ),
    },
    {
      name: t("PILGRIMS_WITHOUT_PASSPORT_COUNT"),
      key: "pilgrims_without_passport_count",
      render: ($, $$, index) => (
        <Controller
          name={`nationality_passport_details.${index}.pilgrims_without_passport_count`}
          control={control}
          rules={{
            required: t("FIELD_REQUIRED"), ...numbersOnlyRules(t),
            validate: validateActualPassportsCount,

          }}
          render={({ field, fieldState: { invalid, error }, formState: { errors } }) => (
            <Tooltip
              color="red"
              placement={isDesktop ? "left" : "top"}
              open={invalid}
              title={error?.message}
            >
              <AntInput
                {...field}
                placeholder={t("PILGRIMS_WITHOUT_PASSPORT_COUNT")}
                status={invalid || checkNationalityPassportDetailsValidateError(errors) ? "error" : undefined}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      name: t("ACTIONS"),
      key: "actions",
    },
  ];

  const getNationalityPassportActions = (rowData, index) => {
    return ([
      {
        key: "delete",
        label: (
          <Button onClick={() => remove(index)} className="btn btn-danger btn-icon" variant="danger">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
          </Button>
        ),
        onClick: () => { },
        danger: true,
      },
    ])
  };

  return (
    <div id="bags-form">
      <Controllers
        watch={form.watch}
        isLoading={isLoading}
        isViewMode={isViewMode}
        controllers={ctls.map((c) => ({
          ...c,
          controllerProps: {
            ...c.controllerProps,
            control,
            errors,
          },
        }))} />

      <Stack gap={2} className="mt-3">

        <Flex justify="space-between" align="center">
          <span className="fw-semibold">{t("NATIONALITY_PASSPORT_DETAILS")}</span>
          {!isViewMode && (
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={() => {
                form.clearErrors("nationality_passport_details");
                append(nationalityPassportDetailDefaultRow)
              }}
            >
              {t("ADD_NATIONALITY_ROW")}
            </Button>
          )}
        </Flex>

        <Controller
          name="nationality_passport_details"
          control={control}
          rules={{
            required: {
              value: true,
              message: t("MIN_LENGTH", { min: 1 }),
            },
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <Stack>
                {!!error && <Alert message={error?.message || error?.root?.message} type="error" showIcon />}
                {!!fields?.length && <TableC
                  columnsFilter={false}
                  data={fields ?? []}
                  columns={nationalityPassportColumns}
                  actions={getNationalityPassportActions}
                />}
              </Stack>
            )
          }}

        />


      </Stack>
    </div>
  );
};

const SelectGroups = (props) => {
  const form = useFormContext();
  const items = form.watch("classification_type");
  const [name, setName] = useState('');
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const onNameChange = (event) => {
    setName(event.target.value);
  };

  const addItem = (e) => {
    e.preventDefault();
    const classification_type = form.getValues("classification_type");
    if (classification_type?.some(item => item?.toLowerCase() === name?.toLowerCase()) || !name?.trim()) {
      return;
    }
    setName('');
    form.setValue("classification_type", [...(classification_type ?? []), name]);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };


  const options = items?.map((item) => ({
    value: item,
    label: item,
  }));

  return (
    <>

      <style>{`
  .ant-select-selector * {
    min-height: 48px !important;
    display: flex !important;
    align-items: center !important;
  }
`}</style>
      <AntSelect
        mode="multiple"
        {...props}
        // tagRender={TagRender}
        getPopupContainer={() => document.getElementById("bags-form")}
        popupRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <Space style={{ padding: '0 8px 4px' }}>
              <AntInput
                placeholder={t("ADD_GROUP")}
                ref={inputRef}
                value={name}
                onChange={onNameChange}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <Button type="button" onClick={addItem}>
                {t("ADD_GROUP")}
              </Button>
            </Space>
          </>
        )}
        id="classification_type"
        placeholder={t("GROUPS")}
        allowClear
        options={options}
      />
    </>
  )
}
const CreateBagModal = (props) => {
  const { arrival, handleCloseBagModal, handleSelectBag, bag, handleCloseUpdateBagModal, params, clearArrival, clearBags } = props;
  const { confirm, ModalComponent } = useConfirm();
  const form = useForm({
    defaultValues: {
      classification_type: [],
      nationality_passport_details: [],
    },
  });
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();

  // =================
  // Side Effects
  // =================
  const queryClient = useQueryClient();
  const { printBagAction } = usePrintBag();

  const { mutate: deleteBagMutation, isPending: isDeleteBagPending } = useMadinaDeleteBagMutation();
  const { mutate: createBag, isPending: isCreateBagPending } = useCreateBagMutation(params);
  const { mutate: updateBag, isPending: isUpdateBagPending } = useUpdateBagMutation(params);
  const { data: bagRes, isLoading: isBagLoading, refetch } = useGetBagsQuery({
    arrival_id: arrival?.request_number,
  });
  const bags = isBagLoading ? Array.from({ length: 10 }, (_, index) => ({ id: index, loading: true })) : bagRes?.data?.data || [];

  useEffect(() => {
    const pickedFields = PickKeyPairs(bag || {}, ["bag_number", "expected_pilgrims_count", "actual_passports_count", "classification_type", "notes", "pilgrims_without_passport_count", "passport_images_count", "nationality_passport_details"]);
    form.reset(bag ? pickedFields : bagDefaultValues);
  }, [bag]);


  const onSubmit = (data) => {
    if (!arrival) return;
    if (bag?.id) {
      updateBag({
        request_number: arrival.request_number,
        id: bag.id,
        ...data,
      }, {

        onSuccess: (data, variables) => {
          clearBags()
          toast.success(t("BAG_UPDATED_SUCCESSFULLY"));
          queryClient.invalidateQueries({ queryKey: ["madina-arrivals"] });

          const newBag = data.data.data;
          queryClient.setQueryData(["one-bag", variables.request_number], (old) => {
            const payload = produce(old, (draft) => {
              draft.data.data = draft.data.data?.map((bag) => {
                if (bag.id === variables.id) {
                  return newBag;
                }

                return bag;
              });
            });


            return payload;
          });
        },
        onError: (err) => {
          handleFormErrors(err, form.setError, t, "FAILD_TO_SAVE")
        }
      });
    } else {
      createBag({
        request_number: arrival.request_number,
        ...data,
      }, {
        onSuccess: () => {
          form.reset(bagDefaultValues);
        },
        onError: (err) => {
          handleFormErrors(err, form.setError, t, "FAILD_TO_SAVE")
        }
      });
    }
  }
  const columns = [

    {
      name: t("BAG_NUMBER"),
      key: "bag_number",
    },
    {
      name: t("EXPECTED_PILGRIMS_COUNT"),
      key: "expected_pilgrims_count",
      render: (value, rowData) => {

        return <Space >
          <Info title={
            <ul>
              <li>{t("PASSPORT_IMAGES_COUNT")}: {getLocalizedNumber(rowData?.passport_images_count) || "-"}</li>
              <li>{t("PILGRIMS_WITHOUT_PASSPORT_COUNT")}: {getLocalizedNumber(rowData?.pilgrims_without_passport_count) || "-"}</li>
              <li>{t("ACTUAL_PASSPORTS_COUNT")}: {getLocalizedNumber(rowData?.actual_passports_count) || "-"}</li>
            </ul>
          } />
          <LabelValue.Value value={value} />
        </Space>
      },
    },
    // {
    //   name: t("ACTUAL_PASSPORTS_COUNT"),
    //   key: "actual_passports_count",
    // },
    {
      name: t("NOTES"),
      key: "notes",
    },
    {
      name: t("CREATOR"),
      key: "creator",
      render: (value) => {
        return getLocalizedText(value?.name_ar, value?.name_en) || "-";
      },
    },
    {
      name: t("ACTIONS"),
      key: "actions",
    },
  ]

  const getActions = (bag) => {
    return [
      {
        key: "edit",
        label: (
          <div role="button"
            onClick={() => handleSelectBag(bag)} className="d-flex align-items-center gap-2">

            <HugeiconsIcon size={14} icon={Edit02Icon} />
            {t("EDIT")}
          </div>
        ),
      },
      printBagAction({ request_number: arrival.request_number, id: bag.id, bag_number: bag.bag_number }),
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => {
          confirm({
            title: t("CONFIRM"),
            content: t("CONFIRM_DELETE_BAG_MESSAGE"),
            okText: t("CONFIRM"),
            cancelText: t("CANCEL"),
            onOk: ({ closeModal }) => {

              deleteBagMutation(
                { request_number: arrival.request_number, bag_id: bag.id },
                {
                  onSuccess: () => {
                    closeModal();
                    refetch();
                  },
                }
              );
              return false;
            },

            onCancel: () => { },
          });
        },
        danger: true,
      },
    ];
  }

  const bagsLength = bags.length;

  return (
    <FormProvider {...form}>
      <ModalComponent loading={isDeleteBagPending} />
      <form onSubmit={(e) => {
        form.handleSubmit(onSubmit)(e)
      }
      } className="relative">
        <div >
          <h3>{bag?.id ? t("EDIT_BAG") : t("BAGS")}</h3>
          <Divider />
        </div>

        {bagsLength < 5 &&
          <>
            <BagsForm />
            <Divider />
          </>
        }

        {!bag?.id && <TableC columnsFilter={false} columns={columns} data={bags} loading={isBagLoading} actions={getActions} />}

        <ActionsFooter style={{ transform: "translateY(16px)" }}>
          <div className="w-100">
            <Flex gap={8}>
              <Button type="submit" variant="primary" className="btn-sm" disabled={isCreateBagPending}>
                {isCreateBagPending || isUpdateBagPending ? <Spinner size="sm" /> : bag?.id ? t("UPDATE") : t("SAVE")}</Button>
              <Button disabled={isCreateBagPending || isUpdateBagPending} style={{ pointerEvents: isCreateBagPending || isUpdateBagPending ? 'none' : 'auto' }} onClick={bag?.id ? handleCloseUpdateBagModal : handleCloseBagModal} type="button" variant="danger" className="btn-sm">{t("CANCEL")}</Button>
            </Flex>
          </div>
        </ActionsFooter>
      </form>
    </FormProvider>
  )
}
export const usePrintBag = () => {
  const { mutate: printBagMutation, isPending: isPrintBagPending } = useMadinaPrintBagMutation();
  const { t } = useTranslation();
  const printBagAction = (payload) => (
    {
      key: "print",
      label: (
        <div className="d-flex align-items-center gap-2">
          <HugeiconsIcon size={16} icon={PrinterIcon} />
          {t("PRINT")}
        </div>
      ),
      onClick: () => {
        const toastId = toast.loading(t("PRINTING_BAG"));
        printBagMutation(payload, {
          onSuccess: () => {
            toast.update(toastId, {
              render: t("PRINT_BAG_SUCCESSFULLY"),
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            });
          },
          onError: () => {
            toast.update(toastId, {
              render: t("PRINT_BAG_FAILED"),
              type: 'error',
              isLoading: false,
              autoClose: 3000,
            });
          },
        });
      },
    })

  return {
    printBagMutation,
    isPrintBagPending,
    printBagAction,
  }
}
export const usePrintBagList = () => {
  const { mutate, isPending: isPrintBagPending, variables } = useMadinaPrintBagListMutation();
  const { t } = useTranslation();

  const printBagsMutation = (payload) => {
    const toastId = toast.loading(t("PRINTING_BAGS"));

    mutate(payload, {
      onSuccess: () => {
        toast.update(toastId, {
          render: t("PRINT_BAGS_SUCCESSFULLY"),
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      },
      onError: (err) => {
        toast.update(toastId, {
          render: err?.message || t("PRINT_BAGS_FAILED"),
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      },
    })
  }

  return {
    printBagsMutation,
    isPrintBagPending,
    variables
  }
}
export const RequestModalCRUD = () => {
  const { t } = useTranslation();
  const form = useForm();
  const { id, mode } = useParams();
  const location = useLocation();
  const { search } = location;
  const open = Object.values(MODES).includes(mode);

  // =================
  // Side Effects
  // =================
  const { data: reportData, isLoading: isReportLoading } = useMadinaOneReportQuery({ id });
  const report = reportData?.data?.data || undefined;

  useEffect(() => {
    const pickedFields = PickKeyPairs(report || {}, [
      "request_number",
      "flight_id",
      "port_id",
      "haj_mission_id",
      "house_contract_id",
      "group_number",
      "pai_no_of_pilgrims",
      "state_id"
    ]);

    form.reset(pickedFields);

  }, [report]);

  const navigate = useNavigate();

  // =================
  // Side Effects
  // =================
  const { mutate: createReportMutation, isPending: isCreateReportPending } = useMadinaCreateReportMutation();
  const { mutate: updateReportMutation, isPending: isUpdateReportPending } = useMadinaUpdateReportMutation();
  const onSubmit = (data) => {
    if (id) {
      updateReportMutation(data);
    } else {
      createReportMutation(data, {
        onSuccess: (data) => {
          const request = data.data.data;

          navigate(`/app/madina/reception/${request.request_number}/view${search}`);
        },
        onError: (err) => {
          handleFormErrors(err, form.setError, t, "FAILD_TO_SAVE")
        }
      });
    }
  }
  const isPending = isCreateReportPending || isUpdateReportPending;
  const title = {
    edit: t("EDIT_REQUEST"),
    create: t("CREATE_NEW_REQUEST"),
    view: t("VIEW_REQUEST"),
  }

  return (
    <>
      <BtnAsLink link={`create${search}`} icon={<HugeiconsIcon icon={Files01Icon} />} className="btn btn-primary">{t("CREATE_NEW_REQUEST")}</BtnAsLink>
      <Modal backdrop="static" keyboard={false} show={open} size="lg" centered>
        <Modal.Header>
          <Modal.Title>{title[mode]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormProvider {...form} {...{ isLoading: isReportLoading, isViewMode: mode === MODES.view }}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <RequestForm />
              <ActionsFooter>
                {mode !== MODES.view && <Button disabled={isPending} type="submit" className="btn-primary btn btn-sm">
                  {isPending ? <Spinner size="sm" /> : title[mode]}
                </Button>}
                <Link
                  to={`/app/madina/reception${search}`}
                  disabled={isPending}
                  style={{ pointerEvents: isPending ? 'none' : 'auto' }}
                  type="button"
                  className="btn-danger btn btn-sm">
                  {t("CANCEL")}
                </Link>
              </ActionsFooter>
            </form>
          </FormProvider>
        </Modal.Body>
      </Modal>
    </>
  )
}
export const RequestForm = () => {
  const { t } = useTranslation();
  const form = useFormContext();
  const { control, formState: { errors }, isLoading, isViewMode } = form
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  const { id } = useParams();
  const { data: reportData, isLoading: isReportLoading } = useMadinaOneReportQuery({ enabled: false });
  const report = reportData?.data?.data || undefined;

  const ctrls = [
    // {
    //   labelProps: {
    //     children: t("FLIGHT_NUMBER"),
    //   },
    //   controllerProps: {
    //     name: "flight_number",
    //     rules: {
    //       required: t("FIELD_REQUIRED"),
    //       maxLength: {
    //         value: 4,
    //         message: t("MAX_LENGTH", {
    //           max: 4,
    //         }),
    //       },
    //     },
    //     render: ({ field }) => (
    //       <AntInput
    //         {...field}
    //         status={errors.flight_number ? "error" : undefined}
    //         placeholder={t("FLIGHT_NUMBER_PLACEHOLDER")}
    //         id="flight_number"
    //       />
    //     ),
    //   },
    // },
    // {
    //   labelProps: {
    //     children: t("FLIGHT_DATE"),
    //     className: "fw-bold required",
    //   },
    //   controllerProps: {
    //     name: "flight_date",
    //     rules: {
    //       required: t("FIELD_REQUIRED"),
    //     },
    //     render: ({ field }) => (
    //       <DatePicker
    //         size="large"
    //         value={field.value ? dayjs(field.value) : null}
    //         onChange={(date) => {
    //           field.onChange(date);

    //           const dayName = getDayNameInArabic(date);
    //           form.setValue("day_name_ar", dayName);
    //         }}
    //         id="flight_date"
    //         placeholder={t("FLIGHT_DATE")}
    //         status={errors.flight_date ? "error" : undefined}
    //       />
    //     ),
    //   },
    //   renderValue: (value) => {
    //     return value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-";
    //   },
    // },
    // {
    //   labelProps: {
    //     children: t("AIRLINE"),
    //     className: "fw-bold required",
    //   },
    //   controllerProps: {
    //     name: "airline_id",
    //     rules: {
    //       required: t("FIELD_REQUIRED"),
    //     },
    //     render: ({ field }) => (
    //       <SelectCachedOptions
    //         {...field}
    //         id="airline_id"
    //         showSearch
    //         placeholder={t("FLIGHTS_MODAL_AIRLINE_PLACEHOLDER")}
    //         status={errors.airline_id ? "error" : undefined}
    //         optionKey="airlines"
    //         options={(airline) => ({
    //           value: String(airline.id),
    //           label: (
    //             <LabelValue.Value value={`${airline.name} (${airline.code})`} />
    //           ),
    //         })}
    //       />
    //     ),
    //   },
    //   renderValue: () => <LabelValue.Value value={report?.airline_name_ar ?? "-"} />
    // },
    // // 3. Port (select dropdown)
    // {
    //   labelProps: {
    //     children: t("PORT"),
    //     className: "fw-bold required",
    //   },
    //   controllerProps: {
    //     name: "port_id",
    //     rules: {
    //       required: t("FIELD_REQUIRED"),
    //     },
    //     render: ({ field }) => (
    //       <SelectCachedOptions
    //         {...field}
    //         id="port_id"
    //         showSearch
    //         placeholder={t("SELECT_ENTRY_PORT")}
    //         status={errors.port_id ? "error" : undefined}
    //         optionKey="ports"
    //         options={(port) => ({
    //           value: port.id,
    //           label: port.name,
    //         })}
    //       />
    //     ),
    //   },
    //   renderValue: (value) => 
    //     <SelectCachedOptions.Display
    //       value={value}
    //       campareBy="id"
    //       optionKey="ports"
    //       render={(port) => [port?.id, port?.name].filter(Boolean).join(" - ")}
    //     />
    // },
    {
      labelProps: {
        children: t("FLIGHT_CODE"),
        className: "fw-bold",

      },
      controllerProps: {
        name: "flight_id",
        render: ({ field }) => <SelectFlightDetails placeholder={t("FLIGHT_CODE")} {...field} />,
      }
    },

    // 4. Haj Mission (select dropdown)
    {
      labelProps: {
        children: t("ORGANIZER"),
        className: "fw-bold required",
      },
      controllerProps: {
        name: "haj_mission_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="haj_mission_id"
            showSearch
            placeholder={t("DELEGATION")}
            status={errors.haj_mission_id ? "error" : undefined}
            optionKey="missions"
            options={(m) => ({
              value: m.id,
              label: <LabelValue.Value value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")} />,
            })}
          />
        ),
      },
      renderValue: (value) =>
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="missions"
          render={(m) => [m?.id, getLocalizedText(m?.name_ar, m?.name_en)].filter(Boolean).join(" - ")}
        />
    },
    // 5. Housing Contract (select dropdown)
    {
      labelProps: {
        children: t("HOUSING_CONTRACT"),
        className: "fw-bold required",
      },
      controllerProps: {
        name: "house_contract_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="house_contract_id"
            showSearch
            placeholder={t("HOUSING_CONTRACT_NO_PLACEHOLDER")}
            status={errors.house_contract_id ? "error" : undefined}
            optionKey="madinahContracts"
            options={(c) => {
              return ({
                value: c.id,
                label: (
                  <LabelValue.Value
                    value={`${c.id} - ${c.house_commercial_name_ar}`}
                  />
                ),
              })
            }}
          />
        ),
      },
      renderValue: (value) => <Space>
        <Info title={<SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="madinahContracts"
          render={(c) => [c.id, c.house_commercial_name_ar].filter(Boolean).join(" - ")}
        />} />
        <LabelValue.Value value={value ?? "-"} />
      </Space>
    },
    // 6. Group Number (text input)
    {
      labelProps: {
        children: t("GROUP_NUMBER"),
        className: "fw-bold",
      },
      controllerProps: {
        name: "group_number",
        rules: {
          required: undefined,
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            status={errors.group_number ? "error" : undefined}
            placeholder={t("GROUP_NUMBER_PLACEHOLDER")}
            id="group_number"
          />
        ),
      },
    },
    // 7. Number of Pilgrims (number input)
    {
      labelProps: {
        children: t("PILGRIMS_COUNT"),
        className: "fw-bold required",
      },
      controllerProps: {
        name: "pai_no_of_pilgrims",
        rules: {
          required: t("FIELD_REQUIRED"),
          ...numbersOnlyRules(t),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            type="number"
            status={errors.pai_no_of_pilgrims ? "error" : undefined}
            placeholder={t("CAMPAIGNS_FORM_PILGRIMS_COUNT_PLACEHOLDER")}
            id="pai_no_of_pilgrims"
          />
        ),
      },
    },
  ];

  return (
    <Controllers
      isLoading={isLoading}
      isViewMode={isViewMode}
      watch={form.watch}
      {...{
        controllers: ctrls.map((config) => ({
          ...config,
          labelProps: {
            ...config.labelProps,
          },
          controllerProps: {
            ...config.controllerProps,
            rules: {
              required: t("FIELD_REQUIRED"),
              ...(config.controllerProps.rules || {}),
            },
            control,
            errors,
          },
        })),
      }}
    />
  )
}